import type { DonorSidebarTab } from "@/app/(inbox)/components/DonorSidebar/DonorSidebar";
import { TabIndex, TabIndexToUrl } from "@/app/(portfolios)/donors/tabUtils";
import { ruleTriggerToRouteIdMap } from "@/app/utils/rulesFormData";
import { type Draft, getDraftType } from "@/app/utils/useLocalStorageDraft";
import {
  ContactMethod,
  CrmActionCategory,
  type RecKind,
  type RuleTrigger,
} from "@/generated/graphql";

export namespace params {
  export const prospects = "prospects";
  export const group = "group";
  export const review = "review";
}
export namespace urls {
  export type UrlsConstant = Extract<
    (typeof urls)[keyof typeof urls] | (typeof urls.workspace)[keyof typeof urls.workspace],
    string
  >;
  export type UrlsDynamic = Extract<
    (typeof urls)[keyof typeof urls] | (typeof urls.workspace)[keyof typeof urls.workspace],
    Function
  >;

  /**
   * @deprecated use urls.home instead
   */
  export function inbox(): "/" {
    return "/";
  }

  export const home = "/";
  export const completed = "/completed";
  export const donors = "/donors";
  export const reports = "/reports/actions";
  export const mobile = "/mobile";
  // NOTE: assistant mode is different than Mo the Assistant in workspace. We may need to workshop the naming
  export const assistant = "/assistant";

  export namespace workspace {
    export const _root = "/workspace";
    export const templates = "/workspace/templates";
    export const sequences = "/workspace/sequences";
    export const planner = "/workspace/planner";
    export const engagementPlans = "/workspace/engagement-plans";
    export const rules = "/workspace/rules";
    export const profile = "/workspace/profile";
    export const prospecting = "/workspace/prospecting";
    export const settings = "/workspace/settings";
    export const emailSuppression = "/workspace/settings/email-suppression";
    export const syncProcessing = "/workspace/settings/sync-processing";
  }

  export const www = `${process.env.NEXT_PUBLIC_BACKEND_URL}/www`;
  export const wwwStatic = `${process.env.NEXT_PUBLIC_BACKEND_URL}/static/www`;
  export const restoreDismissed = "/restore-dismissed";

  export const categoryToRecSuffix = (category: CrmActionCategory): string => {
    switch (category) {
      case CrmActionCategory.Call:
        return "call";
      case CrmActionCategory.Letter:
        return "letter";
      case CrmActionCategory.Other:
        return "card";
      case CrmActionCategory.Task:
        return "task";
      default:
        return "draft";
    }
  };

  export const contactMethodToRecSuffix = (contactMethod: ContactMethod): string => {
    switch (contactMethod) {
      case ContactMethod.Email:
        return "draft";
      case ContactMethod.Call:
        return "call";
      case ContactMethod.Letter:
        return "letter";
      case ContactMethod.Card:
        return "card";
      case ContactMethod.Task:
        return "task";
      default:
        return "draft";
    }
  };

  export function isMobile(path: string): boolean {
    return path.startsWith(mobile);
  }

  export function recPreScreen(recId: string, donorId: string): string {
    return `/rec/${recId}/${donorId}`;
  }

  export function portfolios(portfolioId?: string | null): string {
    return `/donors/segments${portfolioId ? `/${portfolioId}` : ""}?tab=${TabIndexToUrl[TabIndex.Portfolio]}`;
  }

  export function recManualGroupDetail(recId: string): string {
    return `/rec/${recId}/${params.group}`;
  }

  export function actionNextStep(actionId: string): string {
    return `/mobile/actions/${actionId}`;
  }

  export const prospectNamespace = `/rec/${params.prospects}`;
  export const prospectGroup = `${prospectNamespace}/${params.group}`;

  export function recRuleDraftEdit(
    kind: RuleTrigger,
    recId: string,
    donorId: string,
    draft?: Draft | RecKind,
    category?: CrmActionCategory,
  ): string {
    const ruleRoute = ruleTriggerToRouteIdMap.get(kind)!;
    const suffix = category ? categoryToRecSuffix(category) : (getDraftType(draft) ?? "draft");
    return `/rec/rule/${ruleRoute}/${recId}/${donorId}/${suffix}`;
  }

  export function recAutoGroup(kind: RuleTrigger): string {
    const ruleRoute = ruleTriggerToRouteIdMap.get(kind)!;
    return `/rec/rule/${ruleRoute}`;
  }

  export function recsCadenceStep(cadenceNameSlug: string, cadenceStepSlug: string): string {
    return `/rec/cadence/${cadenceNameSlug}/step/${cadenceStepSlug}`;
  }

  export function backToThread(recId: string, donorId: string): string {
    return `/rec/${recId}/${donorId}`;
  }

  export function recDraftEdit(
    recId: string,
    donorId: string,
    draftOrKind?: Draft | RecKind,
  ): string {
    return `/rec/${recId}/${donorId}/${getDraftType(draftOrKind) ?? "draft"}`;
  }

  export function cadenceRecDraftEdit(
    cadenceSlug: string,
    stepSlug: string,
    recId: string,
    donorId: string,
    draftOrKind?: Draft | RecKind,
  ): string {
    return `/rec/cadence/${cadenceSlug}/step/${stepSlug}/${recId}/${donorId}/${getDraftType(draftOrKind) ?? "draft"}`;
  }

  export function recLogView(recId: string, donorId: string): string {
    return `/completed/rec/${recId}/${donorId}`;
  }

  export function donorDetail(donorId: string, tab?: DonorSidebarTab): string {
    if (tab) {
      return `/donor/${donorId}?tab=${tab}`;
    }
    return `/donor/${donorId}/`;
  }

  export function householdDetail(id: string): string {
    return `/www/household/${id}`;
  }

  export function templateEdit(templateId: string): string {
    return `${workspace.templates}/${templateId}/`;
  }

  export const reviewNamespace = `/rec/${params.review}`;
  export const reviewGroup = `${reviewNamespace}/${params.group}`;
}
